import { emptyPageResponse, PageResponse } from '@/shared/interfaces/page-response'
import { defaultFilter, Filter } from '@/shared/interfaces/filter'
import { Paginator } from '@/shared/models/data/paginator'
import { DataSource } from '@/shared/models/data/data-source'
import { StringMap } from '@/shared/utils/map'
import Vue from 'vue'

export class DataSourcePaginator<T> extends Paginator<T> {
  public filter: Filter = defaultFilter()

  public page = 1
  public pageSize = 10

  public response: PageResponse<T> = emptyPageResponse<T>()

  private dataSource: DataSource<T>

  /**
   * The page is initialized after the initial search has been finished
   */
  private initialized = false

  get filtered(): number {
    return this.response.total
  }

  get items(): T[] {
    return this.dataSource.items
  }

  get total(): number {
    return this.response.total
  }

  constructor(dataSource: DataSource<T>) {
    super()

    this.dataSource = dataSource
  }

  public changePageSize(pageSize: number): this {
    this.pageSize = pageSize

    return this
  }

  public changePage(page: number): this {
    this.page = page

    return this
  }

  public initialize(dataSource?: DataSource<T>): Promise<T[]> {
    if (dataSource !== undefined) {
      this.dataSource = dataSource
    }

    return this.update().finally(() => {
      this.initialized = true
    })
  }

  public isInitializing(): boolean {
    return this.dataSource.isLoading() === true && this.initialized === false
  }

  public isSearching(): boolean {
    return this.dataSource.isLoading() === true && this.initialized === true
  }

  public next(): this {
    const pages = Math.ceil(this.total / this.pageSize)

    if (this.page < pages) {
      this.changePage(this.page + 1)
    }

    return this
  }

  public previous(): this {
    if (this.page > 0) {
      this.changePage(this.page - 1)
    }

    return this
  }

  public categories(categories?: StringMap<boolean>): this {
    if (categories !== undefined) {
      this.filter.categories = categories

      this.filter.filterHasChanged = true
    }

    return this
  }

  public query(term?: string): this {
    if (term !== undefined) {
      this.filter.term = term

      this.filter.canReset = true
      this.filter.filterHasChanged = true
    }

    return this
  }

  public reset(): this {
    Object.keys(this.filter.categories).forEach(key => {
      Vue.set(this.filter.categories, key, false)
    })

    this.filter.filterHasChanged = false
    this.filter.canReset = false

    return this
  }

  /**
   * Query the backend for items
   */
  public update(): Promise<T[]> {
    if (this.filter.filterHasChanged || this.filter.queryHasChanged) {
      this.filter.canReset = true
      this.page = 1
    }

    const categoryKeys = Object.entries(this.filter.categories)
      .filter(value => value[1])
      .map(value => Number(value[0]))

    return this.dataSource
      .fetchData({
        categories: categoryKeys,
        page: this.page,
        pageSize: this.pageSize,
        query: this.filter.term,
      })
      .then(response => {
        this.filter.filterHasChanged = false
        this.filter.queryHasChanged = false

        this.response = response

        return response.items
      })
  }
}
